/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, Fragment } from "react";
import { docViewModes, SWITCH_PREVIEW_STATE } from "constants/enums";
import { ClassicPDFView } from "containers/document/ClassicPDFView";
import { NonPDFContainer } from "containers/document/NonPDFContainer";
import { SlideShowContainer } from "containers/document/SlideShowContainer";
import { PageByPageViewContainer } from "containers/document/PageByPageViewContainer";
import { NoPreviewContainer } from "containers/document/NoPreviewContainer";
import { LiquidModeViewContainer } from "../../containers/liquidModeView/LiquidModeViewContainer";
import Wait from "@react/react-spectrum/Wait";
import AcrobatTrefoil from "dc-icons/global/acrobat_trefoil_96.svg";
import { throttle, isUndefined } from "utils/lodashUtils";
import DocumentViewCss from "./DocumentView.css";
import { apiFactory } from "../../api/external/ApiFactory";
import { apiNames } from "../../constants/apiNames";
import { FormattedMessage } from "react-intl";
import { overlayEvents } from "overlays/OverlayEvents";
import * as constants from "constants/constants";
import { HUDContainer } from "components/hud/HUDContainer";
import {
  isCurrentDeviceMobile,
  isCurrentDeviceTablet,
  isTouchDevice,
  isIOS
} from "dc-preview-utils/src/Utils/DeviceUtils";
import * as stores from "stores";
import { getScrollbarSize } from "../../selectors/appUiStateSelectors";
import { pinchZoomManager } from "pinchZoom/index";
import FallbackPrintProgressView from "print/FallbackPrintProgressView";
import { ProgressBarContainer } from "containers/progressBar/ProgressBarContainer";
import { persistEvent } from "utils/EventHandlingUtils";
import { FilenameToastContainer } from "containers/document/FilenameToastContainer";
import { toastHideManager } from "utils/ComponentHideManager";
import { fileNameToastReadyPromise } from "utils/DocumentViewUtils";
import { isSinglePageMode, isAlternateAssetURIAvailable, isFullScreenMode } from "selectors/documentSelectors";
import { CSSTransition } from "react-transition-group";
import { QuickToolsContainer } from "../../containers/quickTools/QuickToolsContainer";
import { subscribeToPageLoader, unsubscribeFromPageLoader } from "services/PageLoaderService";
import { ForwardTabbingContainer } from "containers/accessibility/ForwardTabbingContainer";
import { BackwardTabbingContainer } from "containers/accessibility/BackwardTabbingContainer";
import StickyToolsContainer from "containers/stickyTools/StickyToolsContainer";
import FTECoachmark from "components/FTE/FTECoachmark/FTECoachmark";
import ResizeObserver from "resize-observer-polyfill";
import { withDelay } from "components/hocs/withDelay";
import PopupContainer from "../../containers/popup/PopupContainer";
import BottomPanelContainer from "../../containers/bottomBoard/BottomPanelContainer";
import FABContainer from "containers/FAB/FABContainer";
import BottomBannerDropinWrapperContainer from "containers/bottomBoard/BottomBannerDropinWrapperContainer";

class DocumentView extends Component {
  constructor(props) {
    super(props);
    this.bgColor = null;
    this.throttleMouseMove = throttle(evt => props.handleMouseMove(evt), 50);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.performFilenameToastOps();
    this.performFilenameToastOps = this.performFilenameToastOps.bind(this);
    this.setDocViewRects = this.setDocViewRects.bind(this);
    this.handleMouseMove = evt => {
      persistEvent(evt);
      this.throttleMouseMove(evt);
    };
    this.docTitle = "";

    this.state = {
      quickToolsLoaded: false,
      pageLoader: null
    };
    this._getPageLoader = this._getPageLoader.bind(this);
    this._setPageLoaderState = this._setPageLoaderState.bind(this);
    this.isMobile = isCurrentDeviceMobile();
    this.toolsPanelRef = React.createRef();
    this._getBottomBannerView = this._getBottomBannerView.bind(this);
  }

  setDocViewRects() {
    if (this.elementHandle) {
      const rects = this.elementHandle.getBoundingClientRect();
      this.props.setDocViewRects(rects);
      return rects;
    }
  }

  _focus() {
    // focus the inner pages in view (in case of classic view)
    if (this.childFocusMethod) {
      this.childFocusMethod();
    }
  }

  handleMouseDown(event) {
    stores.documentStore.setShouldAutoHideToast(true);
    stores.documentStore.setIsMouseOverToast(false);
  }

  performFilenameToastOps() {
    fileNameToastReadyPromise(stores)
      .then(() => {
        toastHideManager.resurfaceToast(stores);
        if (!isTouchDevice()) {
          window.addEventListener("mousedown", this.handleMouseDown, true);
        }
      })
      .catch(() => {});
  }

  setChildFocusMethod(focusMethod) {
    // get a handle to the focus method of the child
    this.childFocusMethod = focusMethod;
  }

  _updateHUDPosition(props) {
    if (!this.elementHandle) return;
    let clientWidth = this.elementHandle.clientWidth,
      d = 0,
      dockedWidth;
    const { isHudDocked, hudWidth } = this.props;
    if (props.fixedMargin.left !== "auto" && props.fixedMargin.right !== "auto") {
      const calculatedWidth =
        clientWidth - (props.fixedMargin.left + props.fixedMargin.right + getScrollbarSize(stores));
      if (!isHudDocked) {
        d = calculatedWidth / 2 - hudWidth / 2;
      }
      dockedWidth = `${calculatedWidth}px`;
    } else {
      if (!isHudDocked) {
        d = clientWidth / 2 - hudWidth / 2;
      }
      dockedWidth = clientWidth;
    }
    props.setHUDLeft(d);
    props.setHUDDockedWidth(dockedWidth);
  }

  shouldUpdateDocumentTitle() {
    if (isAlternateAssetURIAvailable(stores)) {
      return;
    }
    let isFileMode = window.location.pathname === "/link/file/" || window.location.pathname === "/link/file";
    let isSendMode = window.location.pathname === "/link/file/send/" || window.location.pathname === "/link/file/send";
    return isFileMode || isSendMode;
  }

  loadQuickToolsChunk() {
    if (this.props.isPreviewable && this.props.showQuickTools && !this.state.quickToolsLoaded) {
      this.setState(() => ({
        quickToolsLoaded: true
      }));
    }
  }

  setBottomPanelHeight() {
    const bottomPanelHeight = (this.toolsPanelRef && this.toolsPanelRef.current.offsetHeight) || 0;
    this.props.setBottomPanelHeight(bottomPanelHeight);
  }

  componentDidMount() {
    const { showQuickToolsInAppConfig, shouldUseMobileModernViewer } = this.props;
    if (isUndefined(showQuickToolsInAppConfig)) {
      const shouldShowQuickTools = !isCurrentDeviceTablet() && !this.isMobile && !shouldUseMobileModernViewer;
      stores.documentStore.setQuickToolsVisibility(shouldShowQuickTools);
      stores.documentStore.setStickyToolsVisibility(shouldUseMobileModernViewer);
      if (this.props.showStickyTools && this.props.stickyToolsLoaded && shouldUseMobileModernViewer) {
        this.setBottomPanelHeight();
      }
    }
    this.loadQuickToolsChunk();

    if (this.props.enableZoomFeature) {
      pinchZoomManager.addEventListeners(stores);
    }

    if (this.shouldUpdateDocumentTitle()) {
      this.docTitle = document.title;
      if (this.props.documentName) {
        document.title = this.props.documentName;
      }
    }
    // For initial Viewport setting.
    if (this.elementHandle && this.props.setViewPortDimensions) {
      this._updateHUDPosition(this.props);
      const rects = this.setDocViewRects();
      let height = this.elementHandle.clientHeight;
      let width = this.elementHandle.clientWidth;
      if (rects) {
        height = rects.height;
        width = rects.width;
      }
      if (
        !this.props.isHUDDisabled &&
        this.props.isHudDocked &&
        !isSinglePageMode(stores) &&
        !isFullScreenMode(stores)
      ) {
        height = Math.max(0, height - constants.HUD_HEIGHT);
      }
      this.props.setViewPortDimensions(width, height);
      this.resizeObserver = new ResizeObserver(entries => {
        this._updateHUDPosition(this.props);
        const rects = this.setDocViewRects();
        if (isIOS() && this.props.pagedViewElementHandle) {
          this.props.pagedViewElementHandle.scrollTop += constants.HUD_HEIGHT;
          this.props.pagedViewElementHandle.scrollTop -= constants.HUD_HEIGHT;
        }
        let height = entries[0].contentRect.height;
        let width = entries[0].contentRect.width;
        if (rects) {
          height = rects.height;
          width = rects.width;
        }

        if (
          !this.props.isHUDDisabled &&
          this.props.isHudDocked &&
          !isSinglePageMode(stores) &&
          !isFullScreenMode(stores)
        ) {
          height -= constants.HUD_HEIGHT;
        }

        this.props.setViewPortDimensions(width, height);
        stores.skinnyRailStore.setFitWidthModeIfNeeded();
      });
      this.resizeObserver.observe(this.elementHandle);
    }
    if (this.elementHandle) {
      this.props.setDocumentView({
        focus: this._focus.bind(this)
      });
      this._registerApis();
      this.props.setDocumentViewHandle(this.elementHandle);
      this._addEventListeners();
    }
    if (isCurrentDeviceMobile() || isCurrentDeviceTablet()) {
      window.addEventListener("resize", this.props.updateAddressBarHeight);
    }
    // set focus initially to correctly handle up/down keys
    this._focus();
    /* Subscribe selectors to page loader logic */
    if (this.props.isModernViewerEnabled && this.props.getShouldShowPageLoader) subscribeToPageLoader();
  }

  componentWillUnmount() {
    pinchZoomManager.removeEventListeners();
    if (this.docTitle) {
      document.title = this.docTitle;
    }
    this.resizeObserver.unobserve(this.elementHandle);
    if (!isTouchDevice()) {
      window.removeEventListener("mousedown", this.handleMouseDown, true);
    }
    if (!this.props.getShouldShowPageLoader) unsubscribeFromPageLoader();
    window.isTimeToViewFirstPageRecorded = false;
  }

  componentDidUpdate(prevProps) {
    this.loadQuickToolsChunk();
    if (prevProps.enableZoomFeature !== this.props.enableZoomFeature) {
      if (this.props.enableZoomFeature) {
        pinchZoomManager.addEventListeners(stores);
      } else {
        pinchZoomManager.removeEventListeners();
      }
    }

    if (
      (prevProps.fixedMargin.left !== this.props.fixedMargin.left &&
        prevProps.fixedMargin.right !== this.props.fixedMargin.right) ||
      prevProps.isHudDocked !== this.props.isHudDocked ||
      prevProps.isHUDDisabled !== this.props.isHUDDisabled ||
      prevProps.hudWidth !== this.props.hudWidth
    ) {
      this._updateHUDPosition(this.props);
    }
    if (
      this.shouldUpdateDocumentTitle() &&
      this.props.documentName &&
      this.props.documentName !== prevProps.documentName
    ) {
      document.title = this.props.documentName;
    }
    // For mobileDevices if the view mode is changing from Single Page to Classic View
    // the viewPortHeight should remain same.
    if (
      !isSinglePageMode(stores) &&
      !(
        isCurrentDeviceMobile() &&
        prevProps.viewMode === docViewModes.SINGLE_PAGE &&
        this.props.viewMode === docViewModes.CLASSIC_VIEW
      )
    ) {
      if (prevProps.isHUDDisabled !== this.props.isHUDDisabled && this.props.isHudDocked) {
        this.props.adjustViewPortHeight(!this.props.isHUDDisabled ? -1 * constants.HUD_HEIGHT : constants.HUD_HEIGHT);
      }
      if (prevProps.isHudDocked !== this.props.isHudDocked && !this.props.isHUDDisabled) {
        this.props.adjustViewPortHeight(this.props.isHudDocked ? -1 * constants.HUD_HEIGHT : constants.HUD_HEIGHT);
      }
      if (prevProps.showProgressBar !== this.props.showProgressBar) {
        this.props.adjustViewPortHeight(
          this.props.showProgressBar ? -1 * constants.PROGRESS_BAR_HEIGHT : constants.PROGRESS_BAR_HEIGHT
        );
      }
    }
    if (
      this.props.isModernViewerEnabled &&
      (prevProps.isReady !== this.props.isReady ||
        prevProps.getShouldShowPageLoader !== this.props.getShouldShowPageLoader)
    ) {
      if (this.props.isReady && this.props.getShouldShowPageLoader) {
        setTimeout(() => {
          this._setPageLoaderState();
        }, 1000);
      } else if (this.props.isReady) {
        this._setPageLoaderState();
      }
    }
    if (this.props.shouldUseMobileModernViewer) {
      const isShowStickyToolsChanged = prevProps.showStickyTools !== this.props.showStickyTools;
      const isStickyToolsLoadChanged = prevProps.stickyToolsLoaded !== this.props.stickyToolsLoaded;

      if (
        (isShowStickyToolsChanged || isStickyToolsLoadChanged) &&
        this.props.stickyToolsLoaded &&
        this.props.showStickyTools
      ) {
        this.setBottomPanelHeight();
      }
    }
  }

  _addEventListeners() {
    if (this.elementHandle !== null) {
      this.elementHandle.addEventListener("touchstart", this.props.onTouchStart, { passive: false });
      this.elementHandle.addEventListener("touchend", this.props.onTouchEnd, { passive: false });
      this.elementHandle.addEventListener("touchcancel", this.props.onTouchCancel, { passive: false });
      this.elementHandle.addEventListener("touchmove", this.props.onTouchMove, { passive: false });
      this.elementHandle.addEventListener("gesturestart", this.props.onTouchStart);
      this.elementHandle.addEventListener("gestureend", this.props.onTouchEnd);
      this.elementHandle.addEventListener("gesturechange", this.props.onTouchMove);
      this.elementHandle.addEventListener("contextmenu", this.props.onContextMenu);
      this.elementHandle.addEventListener("keydown", this.props.onKeyDown);
    }
  }

  _registerApis() {
    apiFactory.registerApi(apiNames.MODIFY_BACKGROUND_COLOR, () => ({
      setBgColor: args => this._modifyBgColor(args),
      resetBgColor: () => {
        this.elementHandle.style.backgroundColor = this.bgColor;
      }
    }));

    apiFactory.registerApi(apiNames.DOC_EVENTS, () => overlayEvents);
  }

  _modifyBgColor(color) {
    this.bgColor = document.defaultView.getComputedStyle(this.elementHandle, null).backgroundColor;
    this.elementHandle.style.backgroundColor = color;
  }

  _setPageLoaderState() {
    this.setState(() => ({
      pageLoader: this._getPageLoader()
    }));
  }

  _wrapTabbingSupport(component) {
    /* These are dummy entry points to intercept tabs 
    so that we can tab into the pdf content */
    return (
      <Fragment>
        <ForwardTabbingContainer />
        {component}
        <BackwardTabbingContainer />
      </Fragment>
    );
  }

  _getDocView() {
    let style = {
      bottom: `${this.props.addressBarHeight / 2}px`
    };
    const waitView = this.props.isModernViewerEnabled ? (
      stores.appStore.customLoader ? (
        <div>{stores.appStore.customLoader}</div>
      ) : (
        <AcrobatTrefoil />
      )
    ) : (
      <FormattedMessage id="file.downloading">
        {txt => (
          <div className={DocumentViewCss.Wait} style={style}>
            <Wait size="L" />
          </div>
        )}
      </FormattedMessage>
    );
    const DelayedWaitView = withDelay(() => waitView, 200);
    let docView = this.props.switchPreviewState === SWITCH_PREVIEW_STATE.SWITCHED ? <DelayedWaitView /> : waitView;
    if (this.props.isReady) {
      switch (this.props.viewMode) {
        case docViewModes.CLASSIC_VIEW:
        case docViewModes.IN_PLACE:
          return <ClassicPDFView setFocusMethod={this.setChildFocusMethod.bind(this)} intl={this.props.intl} />;
        case docViewModes.SINGLE_PAGE:
          return <SlideShowContainer setFocusMethod={this.setChildFocusMethod.bind(this)} intl={this.props.intl} />;
        case docViewModes.NON_PDF:
          return <NonPDFContainer setFocusMethod={this.setChildFocusMethod.bind(this)} />;
        case docViewModes.NO_PREVIEW:
          return <NoPreviewContainer />;
        case docViewModes.FULL_SCREEN:
        case docViewModes.FIT_ONE_FULL_PAGE:
          return (
            <PageByPageViewContainer setFocusMethod={this.setChildFocusMethod.bind(this)} intl={this.props.intl} />
          );
        default:
          return waitView;
      }
    }
    return docView;
  }

  _getPageLoader() {
    return (
      <CSSTransition
        in={this.props.isReady && this.props.isModernViewerEnabled && this.props.getShouldShowPageLoader}
        timeout={1000}
        classNames={{
          appear: DocumentViewCss.modernPageLoaderAppear,
          appearActive: DocumentViewCss.modernPageLoaderAppearActive,
          exit: DocumentViewCss.modernPageLoaderExit,
          exitActive: DocumentViewCss.modernPageLoaderExitActive
        }}
        appear
        unmountOnExit
      >
        <div className={DocumentViewCss.modernPageLoaderBody}>
          <Wait size="S" />
          <div className={DocumentViewCss.modernPageLoaderText}>
            <FormattedMessage id="dialog.documentOpen" />
          </div>
        </div>
      </CSSTransition>
    );
  }

  _getBottomBannerView() {
    if (this.props.shouldShowBottomBanner === true) {
      return <BottomBannerDropinWrapperContainer />;
    } else if (this.props.shouldShowBottomBanner === false) {
      return <BottomPanelContainer />;
    }
    return null;
  }

  render() {
    const { documentStatus } = this.props;
    const docView = this._wrapTabbingSupport(this._getDocView());
    let height = "100%";
    const style = {
      height,
      backgroundColor: this.props.docViewBgColor,
      ...(!this.props.shouldUseMobileModernViewer ? { minHeight: "320px" } : {})
    };

    const quickToolsStyle = {
      visibility: this.props.showQuickTools ? "visible" : "hidden"
    };

    const quickToolsId = this.props.showQuickTools ? "quick-tools-view" : "quick-tools-view-hidden";

    return (
      <React.Fragment>
        {!this.props.isLiquidModeViewEnabled && (
          <div
            className={DocumentViewCss.DocumentView}
            style={style}
            ref={handle => {
              this.elementHandle = handle;
            }}
            onMouseMove={this.handleMouseMove}
            onClickCapture={this.props.onClickCapture}
            id="document-view"
          >
            <PopupContainer />
            {this.props.showToast ? <FilenameToastContainer /> : null}
            <div
              className={this.props.shouldUseMobileModernViewer ? DocumentViewCss.toolsPanelMobile : ""}
              ref={this.toolsPanelRef}
            >
              {this.state.quickToolsLoaded && this.props.showQuickTools ? (
                <QuickToolsContainer id={quickToolsId} style={quickToolsStyle} />
              ) : null}
              {this.props.showStickyTools ? (
                <StickyToolsContainer setBottomPanelHeight={this.setBottomPanelHeight.bind(this)} />
              ) : null}
            </div>
            <FABContainer />
            {!this.props.shouldUseMobileModernViewer &&
            (this.props.isPreviewable || (this.props.isFilePasswordProtected && !this.props.isModernViewerEnabled)) ? (
              <HUDContainer setHUDView={this.props.setHUDView} intl={this.props.intl} />
            ) : null}
            {this.state.pageLoader}
            {docView}
            {this.props.showProgressBar ? <ProgressBarContainer /> : null}
            <div
              role="status"
              aria-live={documentStatus.ariaLive || "assertive"}
              className={DocumentViewCss.docLoadAnnouncement}
            >
              <FormattedMessage id={documentStatus.label} values={{ pageNum: documentStatus.pageNum }}>
                {text => text}
              </FormattedMessage>
            </div>
            <FTECoachmark />
            {this.props.viewMode !== docViewModes.FULL_SCREEN ? this._getBottomBannerView() : null}
          </div>
        )}
        {this.props.isModernViewerEnabled && this.props.isReady && this.props.isPreviewable && (
          <LiquidModeViewContainer />
        )}
        {this.props.showFallbackLoader && <FallbackPrintProgressView />}
      </React.Fragment>
    );
  }
}

export { DocumentView };
